import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { CircleWithSlice } from '~/bootcamp/Activities/Fractions/components/CircleWithSlice/CircleWithSlice'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { getRangesByDifficulty } from './constants'
import { generateTerms } from './helpers'

function FractionCompareDenomEqualFigures({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  autofocus = true,
  actionButtonsFC,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('bootcamp')

  const [answer, setAnswer] = useState<number | string>('')
  const [rightSideSelected, setRightSideSelected] = useState<boolean | null>(null)
  const [isSmallest, setIsSmallest] = useState<boolean>(true)

  const [numerator, setNumerator] = useState<number>(1)
  const [denominator1, setDenominator1] = useState<number>(1)
  const [denominator2, setDenominator2] = useState<number>(3)

  const [taskHistory, setTaskHistory] = useState<number[]>([])

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const { minNumerator, maxNumerator, minDenominator, maxDenominator, resetHistory } =
    getRangesByDifficulty(difficultyLevel)

  useEffect(() => {
    setIsSmallest(Math.random() < 0.5)
    setRightSideSelected(null)
    setAnswer('')
    const termValue = generateTerms(difficultyLevel, taskHistory)
    setNumerator(termValue.numerator)
    setDenominator1(termValue.denominator1)
    setDenominator2(termValue.denominator2)
  }, [difficultyLevel, maxDenominator, maxNumerator, minDenominator, minNumerator, taskHistory])

  // No em dash in the emit text: the backend strips that character.
  // denominator1 is 1 only on the placeholder first render.
  // The two fractions are the answer; they ride the guarded channel.
  useEmitInstance(
    onInstanceChange,
    denominator1 > 1
      ? `two figures, each showing a fraction with numerator ${numerator}; pick the ${
          isSmallest ? 'smallest' : 'largest'
        }`
      : null,
    denominator1 > 1 ? `\\frac{${numerator}}{${denominator1}} and \\frac{${numerator}}{${denominator2}}` : null,
  )

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    let selectedAns = null
    if (isSmallest) {
      selectedAns = Math.min(numerator / denominator1, numerator / denominator2)
      onAnswerStateChange(selectedAns === answer)
    } else {
      selectedAns = Math.max(numerator / denominator1, numerator / denominator2)
      onAnswerStateChange(selectedAns === answer)
    }
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [numerator, ...prevState].slice(0, resetHistory)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
  }

  const handleOnOptionClick = (boolValue: boolean): void => {
    setRightSideSelected(boolValue)
    if (boolValue) {
      setAnswer(numerator / denominator2)
    } else {
      setAnswer(numerator / denominator1)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="flex w-full justify-center  items-center  text-sm font-normal text-black pb-12 ">
          {t('fractions.comparing_figure', {
            largestSmallest: t(isSmallest ? 'fractions.smallest' : 'fractions.largest'),
          })}
        </div>
        <>
          <CircleWithSlice
            className="px-2"
            isSelected={rightSideSelected === false}
            isWrong={rightSideSelected === false && answerState === false}
            numOfDenominator={denominator1}
            numOfNumerator={numerator}
            onClick={() => handleOnOptionClick(false)}
          />
          <CircleWithSlice
            className="px-2"
            isSelected={rightSideSelected === true}
            isWrong={rightSideSelected === true && answerState === false}
            numOfDenominator={denominator2}
            numOfNumerator={numerator}
            onClick={() => {
              handleOnOptionClick(true)
            }}
          />
        </>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => {
            setAnswer('')
            setRightSideSelected(null)
          },
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default FractionCompareDenomEqualFigures
